// Round 25 — auto-formatting for the US-format phone inputs.
//
// THE BUG THIS FIXES: Round 23 added inputMode="tel" to help mobile users, but
// that keypad has no hyphen key — only digits, +, * and #. The strict validator
// requires literal hyphens at fixed positions, so a user typing a phone number
// on a phone could enter digits and had no way to produce the "-" characters
// the error message was demanding. The field was unsatisfiable from the
// keyboard it summoned.
//
// THIS IS FORMATTING, NOT VALIDATION. It lives outside validation/index.ts on
// purpose: PHONE_STRICT_RE, phoneIssues() and isStrictUsPhone() are deliberately
// untouched by this round. The fix only lets a user PRODUCE a value the
// existing validator already accepted — it does not change what is accepted.
//
// US PATH ONLY. The international shape (APO/FPO/DPO and Foreign) requires no
// punctuation at all — a bare digit string already passes — so those fields
// were never broken, and auto-inserting hyphens there would actively corrupt a
// correctly written number like "+44 20 7946 0958". Callers gate on
// addressType; see PhoneInput.tsx.

/** The NANP national number length. */
export const US_PHONE_MAX_DIGITS = 10;

/**
 * Formats whatever the user has typed or pasted into the canonical
 * XXX-XXX-XXXX shape, as far as the digits available allow.
 *
 * NEVER emits a trailing hyphen. Three digits format as "410", not "410-".
 * This is the rule that makes deletion work: if it emitted "410-", backspacing
 * that hyphen would immediately re-add it and the user could never delete past
 * it. Partial values simply stop at the last digit.
 */
export function formatUsPhoneInput(raw: string): string {
  let digits = String(raw ?? "").replace(/\D/g, "");

  // NANP country code. Without this, pasting "+1 410 555 1234" gives 11 digits,
  // the cap below keeps the wrong ten, and the result is "141-055-5123" — a
  // silently mangled number, worse than the bug being fixed. Applied only at
  // exactly 11 digits, so a partially-typed number is never second-guessed.
  if (digits.length === 11 && digits.startsWith("1")) digits = digits.slice(1);

  digits = digits.slice(0, US_PHONE_MAX_DIGITS);

  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
}

/** How many digit characters appear before `caret` in `value`. */
export function digitsBefore(value: string, caret: number): number {
  const end = Math.max(0, Math.min(caret, value.length));
  let n = 0;
  for (let i = 0; i < end; i++) {
    const c = value.charCodeAt(i);
    if (c >= 48 && c <= 57) n++;
  }
  return n;
}

/**
 * Index in `formatted` just after its nth digit — where the caret belongs once
 * the value has been reformatted.
 *
 * Counting DIGITS rather than characters is what survives a reformat: the
 * hyphens move, the digits do not. Without this the cursor jumps to the end on
 * every keystroke, which makes fixing a typo mid-number impossible.
 */
export function caretAfterDigits(formatted: string, digits: number): number {
  if (digits <= 0) return 0;
  let seen = 0;
  for (let i = 0; i < formatted.length; i++) {
    const c = formatted.charCodeAt(i);
    if (c >= 48 && c <= 57) {
      seen++;
      if (seen === digits) return i + 1;
    }
  }
  return formatted.length;
}
